import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, InputGroup, Modal, Row, } from 'react-bootstrap';
import { FaCircleCheck, FaCopy, FaCheck, FaFileContract, FaCreditCard, FaTriangleExclamation, FaCircleExclamation, FaHouse, FaMagnifyingGlass, FaCompass, FaCar, FaHeadset, FaShieldHalved, FaClock, FaMobileScreenButton, FaCoins } from 'react-icons/fa6';
import { InputMask } from 'primereact/inputmask';
import { format, layout, http, validate, extract, messages, triggerConfetti, } from '@/helpers/index';
import { auth } from '@/services/auth';
import Buttons from '@/components/buttons/group';
import ClipboardCopy from './clipboard-copy';
import Divider from './divider';
import Image from 'next/image';
import { DateTime } from 'luxon';
import PaymentConfirmationModal from '@/components/modals/payment-confirmation';
import Turnstile from 'react-turnstile';
import { constant } from '@/helpers/constant';

export default function QuickInquiry({ options, }) {
  const { product, plate, tag, ignoreTitle, size, } = options;
  const user = auth.store.logged.get();
  const [billing, setBilling] = useState();
  const [invalid, setInvalid] = useState();
  const [addCoupon, setAddCoupon] = useState(false);
  const [coupon, setCoupon] = useState();
  const [copied, setCopied] = useState();
  const [countdown, setCountdown] = useState();
  const [expired, setExpired] = useState(false);
  const [order, setOrder] = useState();
  const [history, setHistory] = useState();
  const [optional, setOptional] = useState([]);
  const [modalShow, setModalShow] = useState();
  const [paymentType, setPaymentType] = useState('PIX');
  const isCashbackEligible = user && (!user.partner || Boolean(user.prepaid));
  const userCashback = isCashbackEligible ? (user.cashback || 0) : 0;
  const [useCashback, setUseCashback] = useState(false);
  const couponRef = useRef();
  const { value, name } = product || {};
  const [state, setState] = useState({ items: [{ quantity: 1, value, name }], coupon: {}, total: value, balance: value, cashbackUsed: 0 });
  const [card, setCard] = useState({ cvc: '', expiry: '', document: '', focused: 'number', name: user?.name ?? '', number: '', issuer: '', email: '', plate, });
  const [turnstile, setTurnstile] = useState();

  const calculateBalance = (totalValue, couponDiscount = 0, withCashback = useCashback) => {
    const afterCoupon = couponDiscount > 0 ? (totalValue - (totalValue * couponDiscount)) : totalValue;
    if (withCashback && userCashback > 0) {
      const discount = Math.min(userCashback, afterCoupon);
      return {
        balance: Math.max(0, afterCoupon - discount),
        cashbackUsed: discount,
      };
    }
    return {
      balance: afterCoupon,
      cashbackUsed: 0,
    };
  };

  const handleUseCashback = (checked) => {
    setUseCashback(checked);
    const data = { ...state };
    const couponDiscount = data.coupon?.discount || 0;
    const calc = calculateBalance(data.total, couponDiscount, checked);
    data.balance = calc.balance;
    data.cashbackUsed = calc.cashbackUsed;
    setState(data);
  };

  const runCountdown = (value) => {
    const end = DateTime.fromJSDate(new Date(value.createdAt)).plus({ hours: 1 });
    const now = DateTime.now();
    const remaining = end.diff(now, ['minutes']);
    if (remaining.minutes === 0 || remaining.minutes < 0) {
      setCountdown(remaining.toFormat('00:00'));
      setExpired(true);
      return;
    }
    setCountdown(remaining.toFormat('mm:ss'));
    setTimeout(() => runCountdown(value), 1000);
  };

  useEffect(() => {
    if (user) {
      const { email, mobile, document } = user;
      setCard({ ...card, email, mobile, document, });
    }
    const inquiryPlate = layout.inquiry.get();
    if (inquiryPlate) {
      setCard({ ...card, plate: extract.onlyAlphaAndNumbers(inquiryPlate) });
    }
  }, []);

  const handleCoupon = () => {
    const reset = () => {
      const valueState = { ...state };
      valueState.coupon = {};
      delete valueState.items[0].discount;
      const calc = calculateBalance(valueState.total, 0, useCashback);
      valueState.balance = calc.balance;
      valueState.cashbackUsed = calc.cashbackUsed;
      setState(valueState);
      setCoupon();
      setInvalid();
    };
    if (!couponRef.current.value) {
      layout.focus('coupon');
      reset();
      messages.clear();
      return;
    }
    const code = couponRef.current.value;
    http.get(`/coupons/${code.toUpperCase()}`, (result) => {
      if (!result) {
        reset();
        messages.alert('Por favor, informe um cupom válido.', false, true);
        return;
      }
      const data = { ...state };
      data.coupon = result;
      data.items[0].discount = result.discount;
      const calc = calculateBalance(data.total, result.discount, useCashback);
      data.balance = calc.balance;
      data.cashbackUsed = calc.cashbackUsed;
      setState(data);
      setCoupon(result);
      messages.clear();
      couponRef.current.value = '';
    });
  };

  const handleOnChangeSwitch = ({ target }) => {
    setAddCoupon(target.checked);
    if (!target.checked) {
      handleCoupon();
    }
  };

  const handleInputChange = ({ target }) => {
    if (target.name === 'number') {
      target.value = format.creditCardNumber(target.value);
    } else if (target.name === 'expiry') {
      target.value = format.expirationDate(target.value);
    } else if (target.name === 'cvc') {
      target.value = format.cvc(target.value);
    }
    setCard({ ...card, [target.name]: target.value });
  };

  const handleInputFocus = ({ target }) => {
    setCard({ ...card, focused: target.name });
  };

  const handlePaymentType = (value) => {
    setPaymentType(value);
    setCard({ ...card, number: '', expiry: '', cvc: '' });
  };

  const handlePix = () => {
    setPaymentType('PIX');
  };

  const handlePaymentCheck = (item, loop) => {
    if (!loop) {
      layout.loading(true);
    }
    http.get(`/orders/${item.order._id}`, (result, resultError, statusCode) => {
      if (resultError && statusCode === 401) {
        redirect();
        return;
      }
      if (!loop) {
        layout.loading(false);
      }
      if (result && result.status === 'CONFIRMED') {
        messages.clear();
        setModalShow(false);
        layout.loading(false);
        window.open(`/consulta/verificacao/${item.history._id}`);
        window.location.reload();
        return;
      }
      if (modalShow) {
        setModalShow(false);
        messages.clear();
        messages.alert('Seu pagamento ainda não foi processado. Por favor, aguarde alguns instantes e tente confirmar novamente.', false, true);
      }
      if (loop) {
        setTimeout(() => { handlePaymentCheck(item, true); }, 5 * 1000);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    messages.clear();
    const plateFixed = extract.onlyAlphaAndNumbers(card.plate);
    if (plateFixed.length < 7) {
      layout.focus('plate');
      messages.alert('Por favor, informa a placa que deseja consultar.', false, true);
      return;
    }
    if (!validate.plate(plateFixed)) {
      layout.focus('plate');
      return;
    }
    if (paymentType === 'CREDIT-CARD') {
      if (!card.document) {
        layout.focus('document');
        messages.alert('Por favor, informe seu CPF.', false, true);
        return;
      }
      if (card.document && !validate.cpf(card.document)) {
        layout.focus('document');
        messages.alert('Por favor, informe um CPF válido.', false, true);
        return;
      }
    }
    if (!card.mobile || extract.onlyNumbers(card.mobile).length < 11) {
      messages.error('Por favor, informe um Telefone Celular válido.', false, true);
      layout.focus('mobile');
      return;
    }
    if (billing) { return; }
    setBilling(true);
    layout.loading(true, (useCashback && state.balance === 0) ? 'Estamos confirmando sua consulta com cashback. Por favor, aguarde.' : (paymentType === 'CREDIT-CARD' ? 'Estamos processando seu pagamento. Por favor, aguarde.' : 'Estamos gerando o código para pagamento. Por favor, aguarde.'), false, true);
    const formData = [...e.target.elements].filter((d) => d.name).reduce((acc, d) => { acc[d.name] = d.value; return acc; }, {});
    setCard({ ...card, formData });
    const description = [];
    state.items.forEach((item) => {
      const productValue = item.discount ? `de ${format.currency(item.value)} por ${format.currency(item.value - (item.value * item.discount))}` : `= ${format.currency(item.value)}`;
      description.push(`${item.quantity} x ${item.name} ${productValue}`);
    });
    description.push(plateFixed);
    const body = {
      total: state.total,
      value: state.balance,
      description: description.join(' | '),
      couponCode: state.coupon ? state.coupon.code : '',
      couponDiscount: state.coupon ? state.coupon.discount : 0,
      plate: plateFixed,
      cardName: format.capitalize(card.name),
      email: card.email,
      document: card.document,
      mobile: card.mobile,
      productId: product._id,
      type: product.code,
      paymentType,
      tag,
      optional,
      useCashback: Boolean(useCashback && state.cashbackUsed > 0),
      cashbackUsed: state.cashbackUsed || 0,
    };
    if (paymentType === 'CREDIT-CARD') {
      body.cardNumber = card.number;
      body.cardExpiry = card.expiry;
      body.cardCvc = card.cvc;
    }
    http.post('/quick/inquiry', body, (quickData, resultError) => {
      setBilling(false);
      if (quickData) {
        setOrder(quickData.order);
        setHistory(quickData.history);
        layout.loading(false);
        if (quickData.order.status === 'NOT_AUTHORIZED') {
          if (quickData.order.paymentLog) {
            messages.error('Seu pagamento não foi autorizado.', false, true);
            const log = JSON.parse(quickData.order.paymentLog);
            if (log?.data?.errors) {
              log.data.errors.forEach((error) => messages.error(error.description, false, true));
            }
            return;
          }
          messages.error('Seu pagamento não foi autorizado. Por favor, entre em contato com conosco ou tente novamente com outro cartão.', false, true);
          return;
        }
        if (quickData.order.status === 'PENDING') {
          const startMessage = paymentType === 'CREDIT-CARD' ? 'Seu pagamento está sendo processado' : 'Seu código para pagamento foi gerado';
          messages.info(`${startMessage}. Assim que for confirmado, o relatório da consulta será exibido em uma nova aba e também será enviado por e-mail.`, false, true);
          if (paymentType !== 'CREDIT-CARD') {
            triggerConfetti();
            runCountdown(quickData.order);
            handlePaymentCheck(quickData, true);
          }
          return;
        }
        if (quickData.order.status === 'CONFIRMED') {
          triggerConfetti();
          window.open(`/consulta/verificacao/${quickData.history._id}`);
          window.location.reload();
        }
        return;
      }
      layout.loading(false);
      if (resultError?.message) {
        if (!resultError.message.includes('"data":')) {
          messages.alert(resultError.message, false, true);
          return;
        }
        const messageResult = JSON.parse(resultError.message);
        if (messageResult?.data?.errors) {
          messageResult.data.errors.forEach((error) => messages.error(error.description, false, true));
          return;
        }
      }
      messages.error('Ocorreu um erro ao efetuar sua compra. Por favor, tente novamente dentro de alguns minutos.', false, true);
    }, null, {
      'x-turnstile-token': turnstile
    });
  };

  useEffect(() => {
    layout.focus('inquiryPlate');
  }, []);

  return (
    <Container className="py-3">
      {!product && (
        <Row className="justify-content-center py-4">
          <Col lg={8} xl={7}>
            <Card className="border-0 shadow-sm rounded-4 p-4 p-md-5 bg-white text-center quick-inquiry-not-found-card">
              <div className="d-flex justify-content-center mb-3">
                <div className="quick-inquiry-not-found-icon">
                  <FaCircleExclamation className="fs-1" />
                </div>
              </div>

              <div className="mb-2">
                <span className="badge bg-danger-subtle text-danger rounded-pill px-3 py-2 text-uppercase fw-bold fs-7">
                  Consulta Não Encontrada
                </span>
              </div>

              <h1 className="display-6 fw-extrabold text-dark mb-3">
                Ops! Produto de consulta não localizado
              </h1>

              <p className="lead text-secondary fs-6 mx-auto mb-4 max-w-600">
                A consulta veicular solicitada não está disponível no momento ou o endereço digitado pode estar incorreto.
              </p>

              <div className="d-flex align-items-center justify-content-center gap-3 flex-wrap mb-4">
                <Button href="/consultas-e-pacotes" className="btn-gradient-primary rounded-pill px-4 py-3 fw-bold border-0 shadow-sm d-flex align-items-center gap-2">
                  <FaMagnifyingGlass /> Ver Consultas Disponíveis
                </Button>
                <Button href="/" variant="outline-secondary" className="rounded-pill px-4 py-3 fw-bold border-2 d-flex align-items-center gap-2">
                  <FaHouse /> Ir para o Início
                </Button>
              </div>

              <div className="bg-light rounded-4 p-4 border border-light-subtle text-start">
                <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                  <FaCompass className="text-primary" /> O que você deseja fazer agora?
                </h6>
                <Row className="g-3">
                  <Col sm={6}>
                    <a href="/consultas-e-pacotes" className="p-3 bg-white rounded-3 border d-flex align-items-center gap-3 text-decoration-none text-dark shadow-sm hover-lift h-100">
                      <div className="bg-primary-subtle text-primary p-2 rounded-3 d-flex align-items-center justify-content-center">
                        <FaCar className="fs-5" />
                      </div>
                      <div>
                        <strong className="d-block small fw-bold text-dark">Consultar Veículo</strong>
                        <span className="text-muted fs-8">Escolha sua consulta por placa</span>
                      </div>
                    </a>
                  </Col>
                  <Col sm={6}>
                    <a href="/contato" className="p-3 bg-white rounded-3 border d-flex align-items-center gap-3 text-decoration-none text-dark shadow-sm hover-lift h-100">
                      <div className="bg-primary-subtle text-primary p-2 rounded-3 d-flex align-items-center justify-content-center">
                        <FaHeadset className="fs-5" />
                      </div>
                      <div>
                        <strong className="d-block small fw-bold text-dark">Suporte & Ajuda</strong>
                        <span className="text-muted fs-8">Fale com nossa equipe</span>
                      </div>
                    </a>
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>
        </Row>
      )}
      {product && state.items.length > 0 && (
        <Row className="justify-content-center">
          <Col lg={10} xl={9}>
            {!ignoreTitle && (
              <div className="text-center mb-4">
                <span className="about-home mb-2">Consulta veicular em tempo real</span>
                <h1 className="display-5 fw-extrabold text-dark mt-2 mb-1">{product.name}</h1>
                <p className="lead text-secondary small">Preencha os dados abaixo para receber o relatório completo do veículo.</p>
              </div>
            )}
            <Form onSubmit={handleSubmit}>

              {/* Card 1: Dados da Solicitação */}
              <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex align-items-center">
                  <FaFileContract className="text-primary fs-4 me-2" />
                  <h4 className="fw-bold text-dark mb-0 fs-5">Dados da Solicitação</h4>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-3">
                    <Col xs={12} sm={6} md={3}>
                      <Form.Group>
                        <Form.Label htmlFor="inquiryPlate" className="fw-semibold text-secondary small mb-1">Placa do Veículo</Form.Label>
                        <InputMask
                          id="inquiryPlate"
                          name="plate"
                          className="form-control form-control-lg rounded-3 py-2 px-3 fw-bold input-uppercase shadow-sm border-light-subtle"
                          mask="aaa9*99"
                          slotChar=""
                          required
                          value={card.plate}
                          onChange={handleInputChange}
                          placeholder="ABC1D23"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={5}>
                      <Form.Group>
                        <Form.Label htmlFor="email" className="fw-semibold text-secondary small mb-1">E-mail para Recebimento</Form.Label>
                        <Form.Control
                          type="email"
                          id="email"
                          name="email"
                          className="rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                          placeholder="seuemail@exemplo.com.br"
                          required
                          value={card.email}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                      <Form.Group>
                        <Form.Label htmlFor="mobile" className="fw-semibold text-secondary small mb-1">Telefone WhatsApp</Form.Label>
                        <InputMask
                          name="mobile"
                          id="mobile"
                          className="form-control rounded-3 py-2 px-3 shadow-sm border-light-subtle"
                          mask="(99) 99999-9999"
                          slotChar=""
                          placeholder="(00) 00000-0000"
                          value={card.mobile}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Opcionais */}
              {product.optional.length > 0 && (
                <Card className="border-0 shadow-sm rounded-4 mb-4">
                  <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0">
                    <h4 className="fw-bold text-dark mb-0 fs-5">Serviços Opcionais</h4>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3">
                      {product.optional.map((op, index) => (
                        <Col xs={12} sm={6} key={op.key}>
                          <div className="p-3 bg-light rounded-3 border border-light-subtle">
                            <Form.Check
                              type="switch"
                              label={`${op.description} (+ ${format.currency(op.value)})`}
                              id={`optional-${index}`}
                              name="optional"
                              className="fw-bold text-dark mb-1"
                              value={op.key}
                              checked={optional.some((v) => v.key === op.key)}
                              onChange={(e) => {
                                const balance = () => {
                                  let total = product.value;
                                  for (const op of optional) {
                                    total += op.value;
                                    state.items.push({ quantity: 1, value: op.value, name: op.description });
                                  }
                                  setState({ ...state, balance: total, total });
                                };
                                if (e.target.checked) {
                                  optional.push(op);
                                  setOptional([...optional]);
                                  balance();
                                  return;
                                }
                                optional.forEach((op2, i) => {
                                  state.items.splice(state.items.findIndex((el) => el.name === op2.description), 1);
                                  if (op2.key === op.key) {
                                    optional.splice(i, 1);
                                  }
                                });
                                setOptional([...optional]);
                                balance();
                              }}
                            />
                            {op.details && <p className="small text-secondary mb-0 ms-4">{op.details}</p>}
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Card 3: Pagamento */}
              <Card className="border-0 shadow-sm rounded-4 position-relative mb-4 overflow-hidden">
                {paymentType === 'PIX' && <div className="ribbon ribbon-top-right"><span>Na hora</span></div>}
                <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex align-items-center">
                  <FaCreditCard className="text-primary fs-4 me-2" />
                  <h4 className="fw-bold text-dark mb-0 fs-5">Forma de Pagamento</h4>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4 align-items-center">
                    <Col xs={12} md={5}>
                      <div className="bg-light p-3 rounded-4 border border-light-subtle">
                        <span className="badge bg-primary text-white rounded-pill px-3 py-1 mb-2 small fw-bold">RESUMO</span>
                        <div className="h5 fw-bold text-dark mb-1">
                          {product.name}
                          {optional?.map((op) => (
                            <span key={op.key} className="ms-1 small text-primary">{`+ ${op.description}`}</span>
                          ))}
                        </div>
                        <div className="my-2">
                          {state.total === state.balance ? (
                            <span className="h2 fw-extrabold text-primary mb-0">{format.currency(state.balance)}</span>
                          ) : (
                            <div className="d-flex flex-column">
                              <span className="text-muted text-decoration-line-through fs-6">{format.currency(state.total)}</span>
                              {state.coupon?.discount > 0 && (
                                <small className="text-success fw-bold">Cupom: -{format.currency(state.total * state.coupon.discount)}</small>
                              )}
                              {state.cashbackUsed > 0 && (
                                <small className="text-success fw-bold">Cashback: -{format.currency(state.cashbackUsed)}</small>
                              )}
                              <span className="h2 fw-extrabold text-primary mb-0 mt-1">{format.currency(state.balance)}</span>
                            </div>
                          )}
                          {product.cashback >= 5 && (
                            <div className="mt-2 text-success small fw-bold">
                              <FaCoins className="text-warning me-1" />
                              + {product.cashback}% de cashback após o pagamento
                            </div>
                          )}
                        </div>
                        <p className="small text-secondary mb-0">{product.description || 'Dados seguros e fornecidos por órgãos públicos e de confiança.'}</p>
                      </div>
                    </Col>

                    <Col xs={12} md={7}>
                      {!order?.payment && (
                        <>
                          <div className="mb-3">
                            <Form.Check type="switch" id="hasCoupon" name="hasCoupon" label="Possuo um cupom de desconto" className="fw-semibold text-secondary" onChange={handleOnChangeSwitch} />
                          </div>
                          {addCoupon && (
                            <div className="mb-3">
                              {invalid && <Badge bg="danger" className="mb-2">{invalid}</Badge>}
                              <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                                <Form.Control
                                  type="text"
                                  id="coupon"
                                  name="coupon"
                                  ref={couponRef}
                                  className="border-0 px-3 py-2"
                                  placeholder={coupon ? `${coupon.code} = ${coupon.discount * 100}%` : 'Digite seu cupom'}
                                />
                                <Button type="button" className="btn-gradient-primary border-0 px-4 fw-bold" onClick={handleCoupon}>
                                  <FaCheck className="me-1" /> Aplicar
                                </Button>
                              </InputGroup>
                            </div>
                          )}

                          {isCashbackEligible && userCashback > 0 && (
                            <div className="p-3 mb-3 bg-success-subtle border border-success-subtle rounded-4 shadow-sm">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div className="d-flex align-items-center gap-1 text-success fw-bold">
                                    <FaCoins className="text-warning flex-shrink-0" />
                                    <span>Saldo de Cashback: {format.currency(userCashback)}</span>
                                  </div>
                                  <small className="text-secondary d-block mt-1">
                                    {useCashback && state.balance === 0
                                      ? '100% do valor desta consulta será coberto pelo seu cashback!'
                                      : 'Usar saldo de cashback para abater nesta consulta'}
                                  </small>
                                </div>
                                <Form.Check
                                  type="switch"
                                  id="useCashback"
                                  name="useCashback"
                                  checked={useCashback}
                                  onChange={(e) => handleUseCashback(e.target.checked)}
                                  className="fs-5 ms-3"
                                />
                              </div>
                            </div>
                          )}

                          {useCashback && state.balance === 0 ? (
                            <div className="alert alert-success border-0 rounded-4 shadow-sm p-3 mb-3 text-center">
                              <FaCircleCheck className="text-success fs-3 mb-1" />
                              <h6 className="fw-bold mb-1">Consulta 100% Paga com Cashback!</h6>
                              <p className="small text-secondary mb-0">Nenhum pagamento adicional é necessário. Basta clicar em Concluir abaixo.</p>
                            </div>
                          ) : (
                            <div className="segmented-control p-1 border rounded-pill d-flex align-items-center shadow-sm mb-3">
                              <button
                                type="button"
                                className={`btn rounded-pill flex-fill py-2 fw-bold border-0 ${paymentType === 'PIX' ? 'btn-gradient-primary shadow-sm' : 'text-secondary'}`}
                                onClick={() => handlePaymentType('PIX')}
                              >
                                PIX (Na Hora)
                              </button>
                              <button
                                type="button"
                                className={`btn rounded-pill flex-fill py-2 fw-bold border-0 ${paymentType === 'CREDIT-CARD' ? 'btn-gradient-primary shadow-sm' : 'text-secondary'}`}
                                onClick={() => handlePaymentType('CREDIT-CARD')}
                                disabled={state.balance < 5}
                              >
                                Cartão de Crédito
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {paymentType === 'PIX' && order?.payment && (
                        <Row className="my-2 align-items-center">
                          <Col xs={12} sm={5} className="text-center">
                            {!order.payment.pix && <Image src={`data:image/png;base64,${order.payment.encodedImage}`} width={180} height={180} alt="QR Code PIX" className="bg-white p-3 rounded-4 shadow-sm border d-inline-block position-relative" />}
                            {order.payment.pix && <Image src={order.payment.pix.qrcode} alt="QR Code PIX" width={180} height={180} className="bg-white p-3 rounded-4 shadow-sm border d-inline-block position-relative" />}
                            <div className="mt-2 text-muted small d-flex align-items-center justify-content-center gap-1">
                              <FaMobileScreenButton className="text-secondary" />
                              <span>Escaneie com o app do seu banco</span>
                            </div>
                          </Col>
                          <Col xs={12} sm={7} className="text-center text-sm-start mt-3 mt-sm-0">
                            <div className="mb-2">
                              {!expired && (
                                <div className="h4 fw-bold text-primary mb-0">
                                  <div className="d-flex align-items-center justify-content-center mb-3">
                                    {!expired ? (
                                      <div className="d-inline-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                                        <FaClock className="text-primary fs-5" />
                                        <span className="text-muted small fw-medium">Expira em:</span>
                                        <span className="fw-bold font-monospace fs-5 text-dark">{countdown || '--:--'}</span>
                                      </div>
                                    ) : (
                                      <div className="d-inline-flex align-items-center gap-2 bg-danger-subtle text-danger px-3 py-2 rounded-pill border border-danger-subtle">
                                        <FaTriangleExclamation className="fs-5" />
                                        <span className="fw-bold small">Código de Pagamento expirado</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {expired && <div className="h5 fw-bold text-danger text-center">Código de Pagamento expirado</div>}
                            </div>
                            <ClipboardCopy value={order.payment.pix ? order.payment.pix.qrcode_text : order.payment.payload} setCopied={setCopied}>
                              <Button className="btn-gradient-primary rounded-pill px-4 py-2 fw-bold border-0 shadow-sm w-100">
                                <FaCopy className="me-2" />
                                Copia e Cola PIX
                              </Button>
                            </ClipboardCopy>
                            {copied && (
                              <div className="mt-2 text-success small fw-bold d-flex align-items-center justify-content-center justify-content-sm-center">
                                <FaCircleCheck className="me-1" /> Código copiado!
                              </div>
                            )}
                          </Col>
                          <Col xs={12}>
                            <div className="bg-white p-3 rounded-3 border small text-muted mt-2">
                              <div className="fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                                <FaShieldHalved className="text-success" />
                                Como pagar?
                              </div>
                              <ol className="mb-0 ps-3 text-secondary" style={{ fontSize: '0.8rem' }}>
                                <li>Abra o aplicativo do seu banco</li>
                                <li>Escolha a opção <strong>Pix</strong> &gt; <strong>Pix Copia e Cola</strong> ou <strong>Ler QR Code</strong></li>
                                <li>Após pagar, clique em <strong>Confirmar Pagamento</strong>.</li>
                              </ol>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {state.balance > 0 && paymentType === 'CREDIT-CARD' && (
                        <Row className="g-3">
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Label htmlFor="cardName" className="fw-semibold text-secondary small mb-1">Nome do Titular</Form.Label>
                              <Form.Control type="text" id="cardName" name="name" className="rounded-3 py-2 px-3 border-light-subtle shadow-sm" placeholder="Como aparece no Cartão" required value={card.name} onChange={handleInputChange} onFocus={handleInputFocus} />
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Label htmlFor="cardNumber" className="fw-semibold text-secondary small mb-1">Número do Cartão</Form.Label>
                              <Form.Control type="tel" id="cardNumber" name="number" className="rounded-3 py-2 px-3 border-light-subtle shadow-sm" placeholder="0000 0000 0000 0000" pattern="[\d| ]{16,22}" required value={card.number} onChange={handleInputChange} onFocus={handleInputFocus} />
                            </Form.Group>
                          </Col>
                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label htmlFor="cardExpiry" className="fw-semibold text-secondary small mb-1">Validade</Form.Label>
                              <Form.Control type="tel" id="cardExpiry" name="expiry" className="rounded-3 py-2 px-3 border-light-subtle shadow-sm" placeholder="MM/AA" pattern="\d\d/\d\d" required value={card.expiry} onChange={handleInputChange} onFocus={handleInputFocus} />
                            </Form.Group>
                          </Col>
                          <Col xs={6}>
                            <Form.Group>
                              <Form.Label htmlFor="cardCvc" className="fw-semibold text-secondary small mb-1">CVC / CVV</Form.Label>
                              <Form.Control type="tel" id="cardCvc" name="cvc" className="rounded-3 py-2 px-3 border-light-subtle shadow-sm" placeholder="123" pattern="\d{3,4}" required value={card.cvc} onChange={handleInputChange} onFocus={handleInputFocus} />
                            </Form.Group>
                          </Col>
                          <Col xs={12}>
                            <Form.Group>
                              <Form.Label htmlFor="cardDocument" className="fw-semibold text-secondary small mb-1">CPF do Titular</Form.Label>
                              <InputMask name="document" id="cardDocument" className="form-control rounded-3 py-2 px-3 border-light-subtle shadow-sm" mask="999.999.999-99" slotChar="" placeholder="000.000.000-00" value={card.document} onChange={handleInputChange} required />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </Card.Body>

                <Card.Footer className="bg-transparent border-top-0 p-4 pt-0 text-center">
                  {order && paymentType === 'PIX' && (
                    <Button type="button" className="btn-gradient-primary rounded-pill px-5 py-3 fw-bold border-0 fs-5 w-100 w-md-auto" onClick={() => { setModalShow(true); }}>
                      Confirmar Pagamento
                    </Button>
                  )}
                  {!order && (
                    <Button type="submit" size="lg" className="btn-gradient-success rounded-pill px-5 py-3 fw-bold border-0 fs-5 w-100 w-md-auto" disabled={!turnstile || (state.balance > 0 && state.balance < 5) || billing}>
                      {turnstile && (
                        <span>
                          {useCashback && state.balance === 0 ? (
                            <>
                              <FaCheck className="me-2" />
                              Concluir Consulta com Cashback
                            </>
                          ) : (
                            'Efetuar Pagamento'
                          )}
                        </span>
                      )}
                      {!turnstile && <span>Validando acesso...</span>}
                    </Button>
                  )}
                </Card.Footer>
              </Card>

              {/* Card 4: Atenção Avisos */}
              <div className="alert alert-warning border-0 shadow-sm rounded-4 p-4 text-center mb-4">
                <FaTriangleExclamation className="text-warning fs-3 mb-2" />
                <h5 className="fw-bold text-dark mb-1">Atenção ao Preenchimento</h5>
                <p className="small text-secondary mb-1">
                  Garanta que os dados estejam corretos, pois mesmo que haja erro na digitação o valor será cobrado.
                </p>
                <div className="fw-bold text-dark small">O resultado da consulta poderá ser visualizado nesta mesma página e também será enviado para o e-mail informado acima.</div>
              </div>

            </Form>
          </Col>
        </Row>
      )}
      <PaymentConfirmationModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConfirm={() => handlePaymentCheck({ order, history })}
        canConfirm={!!order}
      />

      <Turnstile sitekey={constant.turnstile.siteKey} onVerify={(token) => { setTurnstile(token); }} />
    </Container>
  );
}
